// ===== LeadDrawer: side panel with tabs =====
import React from 'react';
import { dinheiro } from './dinheiro.js';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { AtribuicaoResumo, CadeiaTimeline, usePermission, usePermissions, useEscreveNoLead } from './auth.jsx';
import { leadAdmiteExclusao } from './exclusao-de-lead.js';
import { EXTENSOES_ANEXO } from './anexos.js';
import { opcoesDeCadastroDoCliente } from './cliente-do-lead.js';
import { ClienteAvatar, StatusCarteira, TipoChip, whatsappLink } from './clientes.jsx';
import { CollapsibleCard } from './cliente-page.jsx';
import {
  AGENTS, EMISSORAS, EMISSORAS_BY_ID, FUNNELS, MIME_BY_EXT, STAGES_BY_FUNNEL,
  STAGES_PROJETOS, STAGE_NAMES, TIPO_CLIENTE, TIPO_CLIENTE_BY_ID, TRANSPORTE_BY_ID, TRANSPORTE_MODALIDADES, brl, brlExato, dataBR,
} from './data.jsx';
import {
  FormField, InlineDatePicker, Input, ModalShell, Select, Typeahead,
} from './modals.jsx';
import { RevalidacaoModal } from './nomus-sync.jsx';
import { gateStatus } from './pipeline-health.jsx';
import { HeatDot, OriginPill } from './primitives.jsx';
import { openPropostaPdf } from './proposta.jsx';
import { rotuloICMSST } from '../supabase/functions/_shared/proposta-documento.js';
import { quandoRelativo } from './data/api.js';
import { rotuloDaTabela } from './preco-tabela.js';
import { ListaDePrecos } from './lista-de-precos.jsx';

const { useState: useStateDr, useEffect: useEffectDr, useMemo: useMemoDr } = React;

// Selo de envio e leitura da proposta.
//
// A pergunta que ele responde é a que o vendedor faz no telefone antes de
// ligar: "mandei e ele não abriu" é uma conversa completamente diferente de
// "abriu três vezes ontem". Antes disto não havia como saber nem se o e-mail
// tinha saído.
//
// ABERTURA É SINAL, NÃO PROVA DE LEITURA, e o texto tem de sustentar isso:
// servidores de e-mail corporativos varrem links por segurança, o cliente
// encaminha a proposta para o sócio, e o contador conta tudo. Por isso o selo
// diz "aberta", nunca "lida" — e o título explica a ressalva a quem parar o
// mouse em cima, em vez de a esconder.
function SeloDeEnvio({ p }) {
  if (!p?.emailEnviadoEm) return null;

  const enviadaEm = new Date(p.emailEnviadoEm);
  const dia = enviadaEm.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const aberta = !!p.vistaPrimeiraEm;
  const vistas = p.vistas || 0;

  return (
    <div
      className="prop-erp-line"
      style={{ color: aberta ? 'var(--color-ok)' : 'var(--color-ink-soft)' }}
      title={aberta
        ? `Primeira abertura em ${new Date(p.vistaPrimeiraEm).toLocaleString('pt-BR')}. Abertura do link é sinal de interesse, não prova de leitura: servidores de e-mail às vezes abrem links por varredura, e o cliente pode encaminhar a proposta.`
        : `Enviada para ${p.emailEnviadoPara || 'o cliente'}. O link ainda não foi aberto.`}
    >
      <Icon name={aberta ? 'check-circle-2' : 'send'} size={11} />
      {/* A frase fica num <span>: solta dentro do flex, cada pedaco (texto, e-mail, "ainda nao aberta") virava uma
          coluna e, no celular, a linha se partia em pedacos soltos. Fora do celular o span some do layout (styles.css). */}
      <span className="prop-erp-texto">
        {' '}Enviada {dia}
        {p.emailEnviadoPara ? <> para <strong>{p.emailEnviadoPara}</strong></> : null}
        {aberta
          ? <> · aberta {quandoRelativo(p.vistaUltimaEm || p.vistaPrimeiraEm)}{vistas > 1 ? ` · ${vistas} aberturas` : ''}</>
          : <> · <span style={{ fontStyle: 'italic' }}>ainda não aberta</span></>}
      </span>
    </div>
  );
}

function MaskedField({ icon, label, value, monospace, extra }) {
  const [shown, setShown] = useStateDr(false);
  const masked = value ? value.replace(/[a-zA-Z0-9]/g, "•") : "";
  return (
    <div className="field">
      <label>{label}</label>
      <div className={`control ${monospace ? "mono" : ""}`} style={{ paddingRight: extra ? 4 : 12 }}>
        <Icon name={icon} size={14} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} />
        <input readOnly value={shown ? value : masked} className={shown ? "" : "mask"} style={{ flex: 1 }} />
        {extra}
        <button className="reveal-btn" onClick={() => setShown(s => !s)} title={shown ? "Ocultar" : "Mostrar"}>
          <Icon name={shown ? "eye-off" : "eye"} size={14} />
        </button>
        <button className="reveal-btn" title="Copiar"><Icon name="copy" size={14} /></button>
      </div>
    </div>
  );
}

// Definição rápida da próxima ação — inline, com presets, sem abrir o editor do negócio.
const PROXIMA_ACAO_PRESETS = [
  "Ligar para o cliente", "Enviar proposta", "Follow-up", "Agendar reunião",
  "Visita técnica", "Negociar valores", "Aguardando retorno", "Enviar catálogo",
];
function ProximaAcaoInline({ lead, compact }) {
  const store = useStore();
  const [editing, setEditing] = useStateDr(false);
  const [txt, setTxt] = useStateDr(lead.proximaAcao && lead.proximaAcao !== "—" ? lead.proximaAcao : "");
  const [urgente, setUrgente] = useStateDr(!!lead.proximaDue);
  const inputRef = React.useRef(null);

  useEffectDr(() => {
    if (editing) {
      setTxt(lead.proximaAcao && lead.proximaAcao !== "—" ? lead.proximaAcao : "");
      setUrgente(!!lead.proximaDue);
      setTimeout(() => inputRef.current && inputRef.current.focus(), 20);
    }
  }, [editing]);

  // Espera a gravacao. Fechar o editor antes da resposta jogaria fora o que o
  // vendedor acabou de digitar quando o banco recusa -- ele veria o toast de
  // erro e o campo vazio, sem nada para tentar de novo.
  const save = async (textoOverride, urgenteOverride) => {
    const valor = textoOverride != null ? textoOverride : txt;
    const ok = await store.setProximaAcao(lead.id, valor, { due: urgenteOverride != null ? urgenteOverride : urgente });
    if (ok !== false) setEditing(false);
  };
  const temAcao = lead.proximaAcao && lead.proximaAcao !== "—";

  if (!editing) {
    return (
      <div className={`pa-inline ${lead.proximaDue ? "due" : ""} ${compact ? "compact" : ""}`}>
        <button className="pa-current" onClick={() => setEditing(true)} title="Clique para definir a próxima ação">
          <Icon name={lead.proximaDue ? "alert-triangle" : "clock"} size={14} />
          <span className="pa-txt">{temAcao ? lead.proximaAcao : "Definir próxima ação…"}</span>
          {lead.proximaDue && <span className="pa-badge">urgente</span>}
          <Icon name="pencil" size={12} className="pa-edit-ic" />
        </button>
        {temAcao && !compact && (
          <button className="pa-clear" title="Concluir / limpar próxima ação" onClick={() => store.setProximaAcao(lead.id, "", { due: false })}>
            <Icon name="check" size={13} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="pa-editor">
      <div className="pa-editor-row">
        <div className="control" style={{ flex: 1 }}>
          <Icon name="clock" size={14} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} />
          <input ref={inputRef} value={txt} placeholder="Ex.: Ligar para confirmar visita"
            onChange={e => setTxt(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }} />
        </div>
        <button className="btn primary sm" onClick={() => save()}><Icon name="check" size={13} /> Salvar</button>
        <button className="btn ghost sm" onClick={() => setEditing(false)}>Cancelar</button>
      </div>
      <div className="pa-presets-h">Atalhos rápidos</div>
      <div className="pa-presets">
        {PROXIMA_ACAO_PRESETS.map(p => (
          <button key={p} className="pa-chip" onClick={() => save(p)}>{p}</button>
        ))}
      </div>
      <label className="pa-urgente">
        <input type="checkbox" checked={urgente} onChange={e => setUrgente(e.target.checked)} />
        <Icon name="alert-triangle" size={12} /> Marcar como urgente / vencida
      </label>
    </div>
  );
}

function TabDados({ lead, agentName, onEdit, onAddNote }) {
  const escreve = useEscreveNoLead();
  const wpp = whatsappLink(lead.telefone);
  return (
    <div>
      {/* Ficha principal do negócio — informação em destaque */}
      <div className="deal-sheet">
        <div className="ds-row ds-full">
          <span className="ds-k">Nome do negócio</span>
          <span className="ds-v ds-title">{lead.empresa}</span>
        </div>
        <div className="ds-grid2">
          <div className="ds-row">
            <span className="ds-k">Contato</span>
            <span className="ds-v">{lead.contato || "—"}</span>
          </div>
          <div className="ds-row">
            <span className="ds-k">Cargo</span>
            <span className="ds-v">{lead.cargo || "—"}</span>
          </div>
        </div>
        <div className="ds-grid2">
          <div className="ds-row">
            <span className="ds-k">Valor da proposta</span>
            <span className="ds-v ds-money mono" style={{ color: lead.valor > 0 ? "var(--color-ok)" : "var(--color-ink-faint)" }}>{lead.valor > 0 ? brl(lead.valor) : "A definir"}</span>
          </div>
          <div className="ds-row">
            <span className="ds-k">Segmento</span>
            <span className="ds-v">{lead.segmento || "—"}</span>
          </div>
        </div>
      </div>

      {/* Contato — acionável */}
      <div className="ds-section-h">Contato</div>
      <MaskedField icon="mail" label="E-mail" value={lead.email} />
      <MaskedField icon="phone" label="Telefone" value={lead.telefone} monospace
        extra={wpp && (
          <a href={wpp} target="_blank" rel="noreferrer" className="wpp-btn" title="Abrir WhatsApp Web">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.586-5.945C.16 5.335 5.495 0 12.05 0a11.82 11.82 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.554-5.338 11.89-11.893 11.89a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"/>
            </svg>
          </a>
        )}
      />
      <div className="field" style={{ marginTop: 10 }}>
        <label>Próxima ação <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--color-ink-faint)", marginLeft: 4 }}>· clique para editar</span></label>
        <ProximaAcaoInline lead={lead} />
      </div>

      {lead.dynamic && (
        <div style={{ marginTop: 6 }}>
          <div className="json-meta-head">
            <Icon name="cpu" size={12} strokeWidth={2} />
            Metadados do agente · {agentName || "Agente externo"}
          </div>
          <div className="json-meta">
            <div className="row"><div className="k">external_id</div><div className="v" style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}>{lead.externalId}</div></div>
            <div className="row"><div className="k">fonte</div><div className="v">{lead.dynamic.fonte}</div></div>
            <div className="row"><div className="k">fit_comercial</div><div className="v" style={{ textTransform: "capitalize" }}>{lead.dynamic.fit_comercial}</div></div>
            <div className="row"><div className="k">urgencia</div><div className="v" style={{ textTransform: "capitalize" }}>{lead.dynamic.urgencia.replace("_", " ")}</div></div>
            <div className="row"><div className="k">canal_origem</div><div className="v">{lead.dynamic.canal_origem}</div></div>
            <div className="row"><div className="k">score_llm</div><div className="v" style={{ fontFamily: "var(--font-mono)" }}>{lead.dynamic.score_llm}/100</div></div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
        {escreve(lead) && <button className="btn primary" onClick={onEdit}><Icon name="edit" size={13} /> Editar negócio</button>}
        <button className="btn" onClick={onAddNote}><Icon name="message-square" size={13} /> Add nota</button>
      </div>
    </div>
  );
}

function TabVinculos({ lead, onOpenCliente, onOpenEspecificador, onNewCliente }) {
  const store = useStore();
  const escreve = useEscreveNoLead();
  const podeCadastrarCliente = usePermission('clientes.create');
  const cliente = lead.clienteId ? store.getClienteById(lead.clienteId) : null;
  const especificador = lead.especificadorId ? store.getEspecificadorById(lead.especificadorId) : null;
  const tipo = TIPO_CLIENTE_BY_ID[lead.tipoCliente];
  const [picking, setPicking] = useStateDr(null); // null | "cliente" | "esp"

  const clienteOpts = store.clientes.filter(c => !c.isCanal)
    .filter(c => lead.funil === "lojistas" ? c.tipo === "lojista" : c.tipo !== "lojista")
    .map(c => ({ value: c.id, label: c.nome, sub: c.cidade, icon: "users" }));
  const espOpts = store.especificadores.map(e => ({ value: e.id, label: e.nome, sub: `${e.tipo}${e.parceriaRepasse ? ` · ${e.pctRepasse}%` : ""}`, icon: "compass" }));

  // `await` desde que `updateLead` passou a gravar (14/09): sem ele o toast
  // "Cliente vinculado" e a linha na timeline sairiam mesmo quando a RLS
  // recusasse a gravação — exatamente o defeito que os comentários de
  // `moveLeadStage` e do modal de produção já descrevem neste arquivo.
  const linkCliente = async (id) => {
    if (!await store.updateLead(lead.id, { clienteId: id })) return;
    store.appendTimeline(lead.id, { tipo: "field", autorTipo: "humano", autor: store.me?.nome, texto: <>Cliente vinculado ao negócio.</>, when: "agora" });
    store.toast({ kind: "ok", msg: "Cliente vinculado" });
    setPicking(null);
  };
  const linkEsp = async (id) => {
    const esp = store.getEspecificadorById(id);
    if (!await store.updateLead(lead.id, { especificadorId: id, tipoCliente: "projeto_canal", parceriaRepasse: !!esp?.parceriaRepasse, pctRepasse: esp?.pctRepasse || 0 })) return;
    store.appendTimeline(lead.id, { tipo: "field", autorTipo: "humano", autor: store.me?.nome, texto: <>Canal <strong>{esp?.nome}</strong> adicionado ao negócio.</>, when: "agora" });
    store.toast({ kind: "ok", msg: "Canal adicionado" });
    setPicking(null);
  };

  return (
    <div>
      {/* Cliente Final */}
      <div className="vinc-block">
        <div className="vb-h">
          <Icon name="users" size={13} />
          <span>Cliente final · pagador</span>
        </div>
        {cliente ? (
          <button className="vinc-card" onClick={() => onOpenCliente(cliente.id)}>
            <ClienteAvatar cliente={cliente} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nm">{cliente.nome}</div>
              <div className="meta">
                <TipoChip tipo={cliente.tipo} size="sm" />
                <StatusCarteira status={cliente.status} reativacao={cliente.reativacao} />
                <span>· {cliente.cidade}</span>
              </div>
            </div>
            <Icon name="external-link" size={14} style={{ color: "var(--color-ink-faint)" }} />
          </button>
        ) : picking === "cliente" ? (
          <div className="vinc-picker">
            <Typeahead value="" onChange={(v) => v && linkCliente(v)} options={clienteOpts} placeholder="Buscar cliente…" icon="users" />
            <button className="btn ghost sm" onClick={() => setPicking(null)}>Cancelar</button>
          </div>
        ) : (
          <div className="vinc-empty">
            <Icon name="users" size={16} style={{ color: "var(--color-ink-faint)" }} />
            <span>Nenhum cliente vinculado a este negócio.</span>
            {escreve(lead) && <button className="btn sm" onClick={() => setPicking("cliente")}>Vincular cliente</button>}
            {escreve(lead) && podeCadastrarCliente && onNewCliente && (
              <button className="btn sm primary-soft" onClick={() => onNewCliente(opcoesDeCadastroDoCliente(lead, linkCliente))}>Cadastrar cliente</button>
            )}
            {escreve(lead) && !podeCadastrarCliente && (
              <span style={{ flexBasis: "100%", fontSize: 12, color: "var(--color-ink-soft)" }}>
                Seu cargo não cadastra clientes. Peça à ADM de Vendas para cadastrar o cliente deste negócio.
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tipo de cliente */}
      <div className="vinc-block">
        <div className="vb-h">
          <Icon name="tag" size={13} />
          <span>Tipo · classificação para BI</span>
        </div>
        <div className="tipo-row-list">
          {TIPO_CLIENTE.map(t => (
            <div key={t.id} className={`tipo-row ${lead.tipoCliente === t.id ? "on" : ""}`} style={{ borderColor: lead.tipoCliente === t.id ? t.color : undefined, background: lead.tipoCliente === t.id ? t.tone : undefined }}>
              <span className="dot" style={{ background: t.color }} />
              <span className="lbl" style={{ color: lead.tipoCliente === t.id ? t.color : undefined }}>{t.label}</span>
              <span className="funnel">funil · {t.funil}</span>
              {lead.tipoCliente === t.id && <Icon name="check" size={13} style={{ color: t.color, marginLeft: "auto" }} />}
            </div>
          ))}
        </div>
      </div>

      {/* Especificador */}
      <div className="vinc-block">
        <div className="vb-h">
          <Icon name="compass" size={13} />
          <span>Especificador / Canal {tipo?.id !== "projeto_canal" && <em style={{ fontWeight: 400, color: "var(--color-ink-faint)" }}>(opcional)</em>}</span>
        </div>
        {especificador ? (
          <button className="vinc-card" onClick={() => onOpenEspecificador && onOpenEspecificador(especificador.id)}>
            <div className="canal-card-ic" style={{ width: 40, height: 40 }}><Icon name="compass" size={18} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="nm">{especificador.nome}</div>
              <div className="meta">
                {especificador.tipo} · {especificador.responsavel}
                {especificador.parceriaRepasse && <span style={{ color: "var(--color-cool)", fontWeight: 600 }}> · {especificador.pctRepasse}% repasse</span>}
              </div>
            </div>
            <Icon name="external-link" size={14} style={{ color: "var(--color-ink-faint)" }} />
          </button>
        ) : picking === "esp" ? (
          <div className="vinc-picker">
            <Typeahead value="" onChange={(v) => v && linkEsp(v)} options={espOpts} placeholder="Digite o nome do canal…" icon="compass" />
            <button className="btn ghost sm" onClick={() => setPicking(null)}>Cancelar</button>
          </div>
        ) : (
          <div className="vinc-empty">
            <Icon name="compass" size={16} style={{ color: "var(--color-ink-faint)" }} />
            <span>Negócio direto, sem canal intermediário.</span>
            <button className="btn sm" onClick={() => setPicking("esp")}>Adicionar canal</button>
          </div>
        )}
      </div>

      {/* Repasse (se aplicável) */}
      {especificador && lead.parceriaRepasse && (
        <div className="vinc-block">
          <div className="vb-h">
            <Icon name="wallet" size={13} />
            <span>Repasse de parceria</span>
          </div>
          <div className="repasse-card">
            <div className="rc-row">
              <div className="rc-label">Valor estimado</div>
              <div className="rc-val mono">{brl(Math.round((lead.valor || 0) * (lead.pctRepasse / 100)))}</div>
            </div>
            <div className="rc-row">
              <div className="rc-label">% de repasse</div>
              <div className="rc-val mono">{lead.pctRepasse}%</div>
            </div>
            <div className="rc-row">
              <div className="rc-label">Status</div>
              {lead.repasse?.status === "Pendente" ? (
                <span className="rc-status warn"><span className="d" /> Pendente — em Contas a Pagar</span>
              ) : lead.stage === "GANHO" ? (
                <span className="rc-status ok"><span className="d" /> Pago</span>
              ) : (
                <span className="rc-status muted"><span className="d" /> Estimado · ativa só em Ganho</span>
              )}
            </div>
            <div className="rc-info">
              <Icon name="info" size={11} />
              Quando este negócio for movido para <strong>Ganho</strong>, o sistema cria automaticamente o repasse no módulo financeiro.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabTabelaPrecos({ lead }) {
  const store = useStore();
  const cliente = lead.clienteId ? store.getClienteById(lead.clienteId) : null;
  // A tabela GRAVADA no negocio (0077); negocio antigo sem ela usa a do cliente.
  const activeTabelaId = lead.tabelaId || cliente?.tabelaId;
  const tabela = activeTabelaId ? store.getTabelaById(activeTabelaId) : null;

  const seletor = (
    <Select
      value={activeTabelaId || ""}
      onChange={v => store.setLeadTabela(lead.id, v)}
      options={[{ value: "", label: "Escolher tabela…" }, ...(store.tabelasPreco || []).map(t => ({ value: t.id, label: t.nome }))]}
    />
  );

  if (!tabela) {
    return (
      <div className="empty" style={{ padding: 30 }}>
        <Icon name="file-spreadsheet" size={24} style={{ color: "var(--color-ink-faint)", marginBottom: 6 }} />
        <div>{cliente ? <>O cliente <strong>{cliente.nome}</strong> ainda não tem tabela de preço.</> : "Vincule um cliente ou escolha uma tabela para este negócio."}</div>
        <div style={{ marginTop: 12, maxWidth: 300, margin: "12px auto 0" }}>{seletor}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="tabela-head" style={{ borderColor: tabela.cor }}>
        <div className="th-icon" style={{ background: tabela.cor }}><Icon name="file-spreadsheet" size={15} /></div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="th-nm">{tabela.nome}</div>
          <div className="th-meta">{cliente ? <>Aplicada a <strong>{cliente.nome}</strong></> : "Tabela manual do negócio"}</div>
        </div>
        <div className="th-desc mono">{rotuloDaTabela(tabela)}</div>
      </div>

      <div className="tabela-switch">
        <label>Trocar tabela deste negócio</label>
        {seletor}
      </div>

      {/* 20 e o pedido do Kaio (16/10) so para esta aba: a lista de 60 ficava longa demais na gaveta e no celular.
          A aba de tabela do cliente (clientes.jsx) segue no padrao da lista; a busca continua achando qualquer produto. */}
      <ListaDePrecos tabela={tabela} limite={20} />

      <div className="tabela-foot-note">
        <Icon name="info" size={11} />
        {tabela.tipo === "manual"
          ? <>Tabela de preços próprios: cada produto tem o valor cadastrado, sem derivar da base.</>
          : <>Preço calculado sobre a lista base ({rotuloDaTabela(tabela)}) — não é uma cópia, então acompanha a atualização da base.</>}
        {" "}Para mudar o preço de uma venda, edite na <strong>proposta</strong>.
      </div>
    </div>
  );
}

function InlineBantField({ leadId, letter, title, value, placeholder, onUpdate }) {
  const [editing, setEditing] = useStateDr(false);
  const [draft, setDraft] = useStateDr(value || "");
  useEffectDr(() => { setDraft(value || ""); }, [value]);
  const save = () => { onUpdate(draft.trim() || null); setEditing(false); };
  const filled = !!value;
  const pct = filled ? 90 : 0;
  return (
    <div className={`bant-item ${filled ? "done" : "empty"} editable`}>
      <div className="bt-row">
        <div className="bt-letter">{letter}</div>
        <div className="bt-title">{title}</div>
        {!editing && (
          <button className="bt-edit-btn" onClick={() => setEditing(true)} title={filled ? "Editar" : "Preencher"}>
            <Icon name={filled ? "pencil" : "plus"} size={11} />
            {filled ? "editar" : "preencher"}
          </button>
        )}
      </div>
      {editing ? (
        <div className="bt-edit">
          <textarea autoFocus className="bt-input" rows={2} value={draft} onChange={e => setDraft(e.target.value)} placeholder={placeholder}
            onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save(); else if (e.key === "Escape") { setDraft(value || ""); setEditing(false); } }} />
          <div className="bt-edit-actions">
            <button className="btn primary sm" onClick={save}><Icon name="check" size={12} /></button>
            <button className="btn ghost sm" onClick={() => { setDraft(value || ""); setEditing(false); }}><Icon name="x" size={12} /></button>
          </div>
        </div>
      ) : (
        <div className="bt-value" onDoubleClick={() => setEditing(true)} style={{ cursor: "text" }}>{value || <em style={{ color: "var(--color-ink-faint)" }}>{placeholder}</em>}</div>
      )}
      {filled && <div className="bant-bar"><div className="fill" style={{ width: pct + "%" }} /></div>}
    </div>
  );
}

function TabBant({ lead }) {
  const store = useStore();
  const fields = [
    { letter: "B", title: "Budget — Orçamento aprovado?",   key: "budget",    placeholder: "Ex: R$ 400k aprovado" },
    { letter: "A", title: "Authority — Quem decide?",       key: "authority", placeholder: "Ex: CEO é o decisor final" },
    { letter: "N", title: "Need — Necessidade mapeada?",    key: "need",      placeholder: "Ex: Reduzir consumo + manutenção fácil" },
    { letter: "T", title: "Timeline — Prazo do projeto?",   key: "timeline",  placeholder: "Ex: Q3 2026" },
  ];
  const filled = fields.filter(f => lead.bant?.[f.key]).length;
  const overall = Math.round((filled / fields.length) * 100);
  return (
    <div>
      <div style={{ background: "var(--color-bg)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "12px 14px", marginBottom: 14, display: "flex", alignItems: "center", gap: 12 }}>
        <div className="bant-anel" style={{ flexShrink: 0, width: 48, height: 48, borderRadius: "50%", border: "4px solid var(--color-accent-soft)", borderTopColor: "var(--color-accent)", borderRightColor: overall > 50 ? "var(--color-accent)" : "var(--color-accent-soft)", borderBottomColor: overall > 75 ? "var(--color-accent)" : "var(--color-accent-soft)", display: "grid", placeItems: "center", fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: 13 }}>{overall}%</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            <span style={{ whiteSpace: "nowrap" }}>Qualificação BANT</span>
            <span className="bant-edit-chip"><Icon name="pencil" size={9} /> editável</span>
          </div>
          <div style={{ fontSize: 12, color: "var(--color-ink-soft)" }}>
            {overall >= 80 ? "Negócio totalmente qualificado" : overall >= 50 ? "Qualificação parcial — falta informação" : "Qualificação inicial — investigar"}
          </div>
        </div>
        <div className="bant-dica" style={{ fontSize: 11, color: "var(--color-ink-soft)", textAlign: "right" }}>
          Clique em <strong>editar</strong> em qualquer campo<br />
          para atualizar sem trava de estágio.
        </div>
      </div>
      {fields.map(f => (
        <InlineBantField key={f.letter}
          leadId={lead.id}
          letter={f.letter} title={f.title}
          value={lead.bant?.[f.key]} placeholder={f.placeholder}
          onUpdate={(v) => store.updateBant(lead.id, f.key, v)}
        />
      ))}
    </div>
  );
}

function TabAlertas({ lead, onAddAlert }) {
  const store = useStore();
  const leadAlerts = store.alerts.filter(a => a.leadId === lead.id);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14, gap: 8 }}>
        <div style={{ fontSize: 12, color: "var(--color-ink-soft)" }}>{leadAlerts.length} alertas neste negócio</div>
        <button className="btn primary sm" style={{ marginLeft: "auto" }} onClick={onAddAlert}>
          <Icon name="plus" size={13} /> Criar alerta
        </button>
      </div>
      {leadAlerts.length === 0 && (
        <div className="empty">
          <Icon name="bell" size={28} style={{ color: "var(--color-ink-faint)", marginBottom: 8 }} />
          <div>Sem alertas para este negócio.</div>
          <div style={{ fontSize: 11.5, marginTop: 4 }}>Crie um alerta para marcar o próximo passo deste negócio.</div>
        </div>
      )}
      {leadAlerts.map(a => (
        <div key={a.id} className={`alert-item ${a.feito ? "done" : ""}`}>
          <div className="icon-wrap">
            <Icon name={a.feito ? "check" : (a.vencido ? "alert-triangle" : "bell")} size={15} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="a-title" style={{ textDecoration: a.feito ? "line-through" : "none" }}>{a.tipo}</div>
            <div className="a-meta">
              <span><Icon name="clock" size={11} /> {a.quando}</span>
              <span>· {a.canal}</span>
              {a.vencido && !a.feito && <span style={{ color: "var(--color-danger)", fontWeight: 700 }}>Vencido</span>}
            </div>
            {(() => {
              const all = store.users;
              const resp = all.find(u => u.id === a.autor);
              const envs = (a.envolvidos || []).map(id => all.find(u => u.id === id)).filter(Boolean);
              return (
                <div className="a-people">
                  {resp && <span className="a-resp" title={`Responsável: ${resp.nome}`}><span className="ap-av">{resp.initials}</span>{resp.nome.split(" ")[0]}</span>}
                  {envs.length > 0 && (
                    <span className="a-envs" title={"Envolvidos: " + envs.map(e => e.nome).join(", ")}>
                      <Icon name="at-sign" size={10} />
                      {envs.slice(0, 3).map(e => <span key={e.id} className="ap-av sm">{e.initials}</span>)}
                      {envs.length > 3 && <span className="ap-more">+{envs.length - 3}</span>}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
          <div className="a-actions">
            <button className="icon-btn" title="Exportar .ics" onClick={() => store.exportIcs(a)}><Icon name="download" size={14} /></button>
            <button className="icon-btn" title={a.feito ? "Reabrir" : "Marcar feito"} onClick={() => store.toggleAlertDone(a.id)}>
              <Icon name={a.feito ? "refresh" : "check"} size={14} />
            </button>
          </div>
        </div>
      ))}
      <div style={{ marginTop: 14, padding: 12, background: "var(--color-bg)", border: "1px dashed var(--color-border)", borderRadius: "var(--radius-sm)", display: "flex", alignItems: "center", gap: 10 }}>
        <Icon name="calendar-plus" size={18} style={{ color: "var(--color-accent)" }} />
        {/* Nao ha integracao com Google Calendar. Ate 21/09 este bloco anunciava
            "sincronizacao bidirecional ... em sync automatico", e o botao .ics
            ao lado era a unica coisa real aqui: um arquivo que a pessoa importa
            na agenda a mao. */}
        <div style={{ flex: 1, fontSize: 12.5 }}>
          <strong>Levar para a sua agenda</strong>
          <div style={{ color: "var(--color-ink-soft)", fontSize: 11.5 }}>Baixe o .ics e importe no Google Calendar ou no Outlook. Não há sincronização automática.</div>
        </div>
        <button className="btn sm" onClick={() => leadAlerts[0] && store.exportIcs(leadAlerts[0])}><Icon name="download" size={12} /> .ics</button>
      </div>
    </div>
  );
}

function TabHistorico({ lead, onAddNote }) {
  const store = useStore();
  const [filter, setFilter] = useStateDr("all");
  const [nota, setNota] = useStateDr("");
  // A timeline vive em store.timeline — a memória precisa observar esse mapa para
  // que uma nota recém-adicionada apareça na hora.
  const events = useMemoDr(() => store.getTimeline(lead.id), [lead.id, store.timeline, store.leads]);
  const enviarNota = () => { const t = nota.trim(); if (!t) return; store.addNote(lead.id, t); setNota(""); };
  const filtered = events.filter(e => filter === "all" || (filter === "note" && e.tipo === "note") || (filter === "agent" && e.autorTipo === "agente") || (filter === "human" && e.autorTipo === "humano") || (filter === "email" && e.tipo === "email"));

  const iconForType = (t) => ({ stage: "git-merge", criacao: "sparkles", field: "edit", note: "message-square", alert: "bell", bant: "shield-check", email: "mail", tarefa: "check-circle-2" }[t] || "circle");
  const wrapClsForType = (t, autorTipo) => {
    if (t === "email") return "email";
    if (autorTipo === "agente") return "agent";
    if (t === "stage") return "stage";
    if (t === "alert") return "alert";
    if (t === "criacao") return "won";
    return "";
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, gap: 8, flexWrap: "wrap" }}>
        <div className="seg" style={{ margin: 0 }}>
          <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")}>Tudo <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{events.length}</span></button>
          <button className={filter === "note" ? "on" : ""} onClick={() => setFilter("note")}>
            <Icon name="message-square" size={12} strokeWidth={2} /> Notas <span style={{ opacity: 0.7, fontFamily: "var(--font-mono)" }}>{events.filter(e => e.tipo === "note").length}</span>
          </button>
          <button className={filter === "email" ? "on" : ""} onClick={() => setFilter("email")}>
            <Icon name="mail" size={12} strokeWidth={2} /> Gmail
          </button>
          <button className={filter === "agent" ? "on" : ""} onClick={() => setFilter("agent")}>
            <Icon name="cpu" size={12} strokeWidth={2} /> Agente
          </button>
        </div>
        <button className="btn primary sm" onClick={onAddNote}><Icon name="plus" size={12} /> Nota</button>
      </div>

      <div className="tl-composer">
        <textarea className="textarea" rows={2} placeholder="Escreva uma nota e ela entra na timeline…"
          value={nota} onChange={e => setNota(e.target.value)}
          onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") enviarNota(); }} />
        <div className="tl-composer-foot">
          <span className="hint">⌘/Ctrl + Enter para salvar</span>
          <button className="btn primary sm" onClick={enviarNota} disabled={!nota.trim()} style={{ opacity: nota.trim() ? 1 : 0.5 }}>
            <Icon name="message-square" size={12} /> Adicionar nota
          </button>
        </div>
      </div>

      <div className="timeline">
        {filtered.map((e, i) => (
          <div key={i} className="tl-item">
            <div className={`tl-icon ${wrapClsForType(e.tipo, e.autorTipo)}`}>
              <Icon name={iconForType(e.tipo)} size={13} strokeWidth={2} />
            </div>
            <div className="tl-body">
              <div className="tl-head">
                {e.autorTipo === "agente" ? (
                  <span className="tl-author agent">
                    <Icon name="cpu" size={11} strokeWidth={2} />
                    {e.autor}
                  </span>
                ) : (
                  <span className="tl-author">{e.autor}</span>
                )}
                <span className="tl-time">{e.when}</span>
                {e.tipo === "email" && <span className="email-tag">Gmail</span>}
              </div>
              <div className="tl-text">{e.texto}</div>
              {e.detail && <pre className="tl-detail">{e.detail}</pre>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="empty">Sem eventos para este filtro.</div>}
      </div>
    </div>
  );
}

function AnexoRow({ anexo, onDownload, onRemove }) {
  const meta = MIME_BY_EXT[anexo.ext] || { label: anexo.ext.toUpperCase(), color: "#64748B", icon: "file", preview: false };
  // Dois cliques para remover (sem janela do navegador): o primeiro arma, o segundo confirma; some sozinho se a pessoa desistir.
  const [armado, setArmado] = React.useState(false);
  const [ocupado, setOcupado] = React.useState(false);
  React.useEffect(() => {
    if (!armado) return undefined;
    const t = setTimeout(() => setArmado(false), 4000);
    return () => clearTimeout(t);
  }, [armado]);
  const baixar = async () => { setOcupado(true); try { await onDownload(anexo); } finally { setOcupado(false); } };
  const remover = async () => {
    if (!armado) { setArmado(true); return; }
    setOcupado(true);
    try { await onRemove(anexo); } finally { setOcupado(false); setArmado(false); }
  };
  return (
    <div className="anexo-row" data-anexo={anexo.id}>
      <div className="anexo-ic" style={{ background: meta.color }}>
        <Icon name={meta.icon} size={14} strokeWidth={2.2} />
        <span className="ext-tag">{meta.label}</span>
      </div>
      <div className="anexo-body">
        <div className="anexo-nm">{anexo.nome}</div>
        <div className="anexo-meta">
          <span className="mono">{anexo.sizeMB} MB</span>
          {anexo.pages && <span>· {anexo.pages} pág.</span>}
          <span>· {anexo.uploadedAt} · {anexo.uploader}</span>
        </div>
      </div>
      <div className="anexo-actions">
        <button className="btn sm" title="Baixar arquivo original" aria-label={`Baixar ${anexo.nome}`} disabled={ocupado} onClick={baixar}>
          <Icon name="download" size={12} />
        </button>
        <button className="btn sm" title="Remover anexo" aria-label={`Remover ${anexo.nome}`} disabled={ocupado} onClick={remover}
          style={armado ? { color: "var(--color-danger)", borderColor: "var(--color-danger)" } : undefined}>
          <Icon name="trash" size={12} />{armado && <span> Confirmar?</span>}
        </button>
      </div>
    </div>
  );
}

function TabAnexos({ lead }) {
  const store = useStore();
  const anexos = store.getAnexosByLead(lead.id);
  const totalMB = anexos.reduce((s, a) => s + (a.sizeMB || 0), 0);
  const [carregando, setCarregando] = React.useState(true);
  const [enviando, setEnviando] = React.useState(false);
  const [arrastando, setArrastando] = React.useState(false);
  const inputRef = React.useRef(null);

  // A lista vem do banco ao abrir a aba (e ao trocar de negocio), nunca da memoria.
  React.useEffect(() => {
    let vivo = true;
    setCarregando(true);
    Promise.resolve(store.carregarAnexos(lead.id)).finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [lead.id, store.carregarAnexos]);

  const enviar = async (arquivos) => {
    if (!arquivos || !arquivos.length || enviando) return;
    setEnviando(true);
    try { await store.enviarAnexos(lead.id, arquivos); } finally { setEnviando(false); }
  };

  return (
    <div>
      <div className="anexos-head">
        <div>
          <div className="ah-nm"><Icon name="paperclip" size={13} /> Anexos do negócio</div>
          <div className="ah-meta">{anexos.length} arquivo{anexos.length === 1 ? "" : "s"} · <span className="mono">{totalMB.toFixed(1)} MB</span></div>
        </div>
      </div>
      <div
        className={`uploader-dz ${arrastando ? "on" : ""}`}
        data-testid="anexo-dropzone"
        role="button" tabIndex={0} aria-disabled={enviando}
        onClick={() => { if (!enviando) inputRef.current?.click(); }}
        onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !enviando) { e.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); setArrastando(true); }}
        onDragLeave={() => setArrastando(false)}
        onDrop={(e) => { e.preventDefault(); setArrastando(false); enviar(e.dataTransfer?.files); }}
        style={{ marginTop: 12, opacity: enviando ? 0.7 : 1, cursor: enviando ? "progress" : "pointer" }}
      >
        <Icon name={enviando ? "loader" : "upload"} size={20} />
        <div className="dz-text">
          {enviando ? <strong>Enviando…</strong> : <><strong>Clique para escolher</strong> ou arraste os arquivos aqui</>}
        </div>
        <div className="dz-hint">
          <span>PDF, DWG, DXF, XLSX, CSV, PNG e JPEG</span>
          <span className="mono">até 50 MB por arquivo</span>
        </div>
        <input ref={inputRef} type="file" multiple hidden accept={EXTENSOES_ANEXO.map((e) => `.${e}`).join(",")}
          onChange={(e) => { const arqs = Array.from(e.target.files || []); e.target.value = ""; enviar(arqs); }} />
      </div>
      <div className="anexos-list">
        {carregando && anexos.length === 0 && (
          <div className="empty" style={{ padding: 28 }}><div>Carregando anexos…</div></div>
        )}
        {!carregando && anexos.length === 0 && (
          <div className="empty" style={{ padding: 28 }}>
            <Icon name="paperclip" size={24} style={{ color: "var(--color-ink-faint)", marginBottom: 6 }} />
            <div>Nenhum arquivo anexado ainda.</div>
          </div>
        )}
        {anexos.map(a => (
          <AnexoRow key={a.id} anexo={a}
            onDownload={store.baixarAnexo}
            onRemove={(x) => store.removerAnexo(lead.id, x.id)}
          />
        ))}
      </div>
    </div>
  );
}

function PropStatusChip({ status }) {
  const map = {
    rascunho:   { lbl: "Rascunho",          cls: "muted", icon: "file" },
    enviada:    { lbl: "Enviada ao Cliente",  cls: "warn",  icon: "send" },
    negociacao: { lbl: "Em Negociação",       cls: "warn",  icon: "message-circle" },
    aprovada:   { lbl: "Aprovada",           cls: "ok",    icon: "check-circle-2" },
    no_nomus:   { lbl: "No Nomus",           cls: "ok",    icon: "server" },
    perdida:    { lbl: "Perdida",            cls: "danger",icon: "x-circle" },
  };
  const m = map[status] || map.rascunho;
  return <span className={`prop-status ${m.cls}`}><Icon name={m.icon} size={10} /> {m.lbl}</span>;
}

// Quem FEZ a proposta (propostas.autor_id), que pode nao ser o dono do negocio:
// a ADM de vendas e o orcamentista fazem proposta pelo closer (Kaio, 25/09:
// "quem movimentou foram as outras funcoes"). O closer segue dono e vendedor.
function autoriaDaProposta(p, users = []) {
  if (!p?.autorId) return null;
  const autor = users.find(u => u.id === p.autorId);
  return `feita por ${autor?.nome || "usuário removido"}`;
}

// Os itens de uma proposta, dentro do card do negocio: FECHADOS por padrao.
// Reteste do Kaio (25/09): "a lista de produtos ainda aparece completa no card
// do negocio, um negocio muito grande dificultaria o uso". O corte anterior (8
// itens) ainda abria a lista inteira em proposta pequena e esticava o painel
// na grande. Agora o card mostra so a contagem; a lista abre sob demanda, numa
// caixa com rolagem.
function ItensDaProposta({ p, anexosLead = [] }) {
  const [aberto, setAberto] = useStateDr(false);
  const itens = p.itens || [];
  if (!itens.length) return null;
  const rotulo = `${itens.length} ${itens.length === 1 ? "item" : "itens"}`;
  return (
    <div className="prop-items-mini">
      <button className="btn sm ghost" onClick={() => setAberto(a => !a)} aria-expanded={aberto}>
        <Icon name={aberto ? "chevron-up" : "list"} size={12} /> {aberto ? `Ocultar os ${rotulo}` : `Ver os ${rotulo}`}
      </button>
      {aberto && (
        <div className="pim-lista" style={{ maxHeight: 320, overflowY: "auto", marginTop: 6 }}>
          {itens.map((it, i) => {
            // suporta modelo novo (nome, quantidade, totalLinha, ambienteId) e antigo (produto, qtd, subtotal, ambiente)
            const nomeProd   = it.nome || it.produto || "";
            const qtd        = it.quantidade != null ? it.quantidade : (it.qtd || 0);
            const unid       = it.unidade || "un";
            const subtot     = it.totalLinha != null ? it.totalLinha : (it.subtotal || 0);
            const ambLabel   = it.ambiente || (it.ambienteId ? (p.ambientes || []).find(a => a.id === it.ambienteId)?.nome : null) || null;
            const anex       = (it.anexoIds || []).map(aid => (anexosLead.find(a => a.id === aid) || {}).nome).filter(Boolean);
            return (
              <div key={i} className="pim-row">
                <div className="pim-main">
                  {ambLabel && <span className="pim-amb">{ambLabel}</span>}
                  <span className="pim-prod">{it.codigo ? <span className="mono" style={{ fontSize: 10, opacity: 0.6, marginRight: 4 }}>{it.codigo}</span> : null}{nomeProd}</span>
                  {anex.length > 0 && <span className="pim-anex"><Icon name="paperclip" size={9} /> {anex.join(", ")}</span>}
                </div>
                <span className="mono pim-qtd">{String(qtd).replace(".", ",")} {unid}</span>
                <span className="mono pim-sub">{brlExato(subtot)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TabPropostas({ lead, onNewProposta, onEditProposta }) {
  const store = useStore();
  const propostas = store.getPropostasByLead(lead.id);
  const cliente = lead.clienteId ? store.getClienteById(lead.clienteId) : null;
  const [expanded, setExpanded] = useStateDr(null);
  const [reval, setReval] = useStateDr(null); // proposta aguardando confirmacao antes do POST /pedidos
  const [pendingErp, setPendingErp] = useStateDr(null);
  // O envio espera um render: approvePropostaToERP lê propostas do store, e os
  // preços alinhados na conferência precisam estar aplicados antes do payload.
  useEffectDr(() => {
    if (!pendingErp) return;
    const id = pendingErp; setPendingErp(null);
    store.approvePropostaToERP(lead.id, id);
  }, [pendingErp]);
  const anexosLead = store.getAnexosByLead(lead.id);
  const selecionada = propostas.find(p => p.selecionadaFechamento);

  return (
    <div>
      <div className="anexos-head">
        <div>
          <div className="ah-nm"><Icon name="file-text" size={13} /> Propostas do negócio</div>
          <div className="ah-meta">{propostas.length} proposta{propostas.length === 1 ? "" : "s"} para {lead.empresa}{selecionada ? <> · <strong style={{ color: "var(--color-ok)" }}>{selecionada.controle} p/ fechamento</strong></> : null}</div>
        </div>
        <button className="btn primary sm" onClick={onNewProposta}><Icon name="plus" size={12} /> Nova proposta</button>
      </div>

      {propostas.length === 0 && (
        <div className="empty" style={{ padding: 28 }}>
          <Icon name="file-text" size={24} style={{ color: "var(--color-ink-faint)", marginBottom: 6 }} />
          <div>Nenhuma proposta criada ainda.</div>
          <div style={{ fontSize: 11.5, marginTop: 4 }}>Monte uma proposta PDLED/PDLIGHT em PDF com os itens da tabela do cliente.</div>
        </div>
      )}

      {propostas.length > 1 && (
        <div className="prop-multi-note">
          <Icon name="info" size={11} />
          <span>Várias propostas em aberto. Selecione <strong>uma para fechamento</strong> — ela define o valor do negócio.</span>
        </div>
      )}

      <div className="prop-list">
        {propostas.map(p => {
          const emissora = EMISSORAS_BY_ID[p.emissoraId] || EMISSORAS[0];
          const open = expanded === p.id;
          const aprovada = p.status === "aprovada";
          return (
            <div key={p.id} className={`prop-card ${p.selecionadaFechamento ? "selected" : ""}`}>
              <div className="prop-card-head" onClick={() => setExpanded(open ? null : p.id)}>
                <div className="prop-emissora-dot" style={{ background: emissora.cor }}>{emissora.nome === "PDLED" ? "PD" : "PL"}</div>
                <div className="prop-card-main">
                  <div className="prop-card-nm">
                    <span className="cp mono">{p.controle}</span> {p.nome}
                    {p.selecionadaFechamento && <span className="prop-sel-badge"><Icon name="check" size={9} /> fechamento</span>}
                  </div>
                  <div className="prop-card-meta">{emissora.nome} · {p.itens.length} itens · validade {p.validade} · {dataBR(p.criadaEm)}{autoriaDaProposta(p, store.users) ? ` · ${autoriaDaProposta(p, store.users)}` : ""}</div>
                  {/* Três estados, não dois. "Em voo" é o que o plano da 3b
                      chama de resposta perdida: o pedido PODE existir no ERP.
                      Mostrar isso como falha convidaria um reenvio, e é o
                      reenvio que cria o segundo CP para a mesma venda. */}
                  {p.erp?.emVoo
                    ? <div className="prop-card-ctrl" style={{ color: "var(--color-warn)" }}>
                        <Icon name="clock" size={10} /> envio em andamento · confira no Nomus antes de tentar de novo
                      </div>
                    : p.erp?.pedidoId
                      ? <div className="prop-card-ctrl"><Icon name="server" size={10} /> Pedido ERP <strong className="mono">{p.erp.pedidoId}</strong></div>
                      : <div className="prop-card-ctrl"><Icon name="hash" size={10} /> nº CP atribuído pelo Nomus ao criar o pedido</div>}
                </div>
                <div className="prop-card-right">
                  <PropStatusChip status={p.status} />
                  <div className="prop-card-total mono">{brlExato(p.total)}</div>
                </div>
                <Icon name={open ? "chevron-up" : "chevron-down"} size={14} style={{ color: "var(--color-ink-faint)" }} />
              </div>

              {open && (
                <div className="prop-card-body">
                  <ItensDaProposta p={p} anexosLead={anexosLead} />
                  <ResumoDeValoresDaProposta p={p} />
                  {p.erp?.enviado && (
                    <div className="prop-erp-line"><Icon name="server" size={11} /> <span className="prop-erp-texto">Controle CRM <strong className="mono">{p.controle}</strong> · pedido ERP <strong className="mono">{p.erp.pedidoId}</strong></span></div>
                  )}
                  <SeloDeEnvio p={p} />
                  {p.temLinkPublico && (
                    <div className="prop-erp-line" style={{ justifyContent: "flex-start" }}>
                      <button className="btn sm" style={{ color: "var(--color-warn)" }}
                        title="Quem tiver o endereço antigo deixa de conseguir abrir. A proposta continua intacta."
                        onClick={() => store.revogarLinkProposta(lead.id, p.id)}>
                        <Icon name="lock" size={12} /> Invalidar link
                      </button>
                    </div>
                  )}
                  <AvisoICMSST p={p} />
                  <ConfirmarModalidade p={p} onConfirmar={(id) => store.updateProposta(lead.id, p.id, { modalidadeTransporte: id, modalidadeNome: TRANSPORTE_BY_ID[id]?.nome || "" })} />
                  <div className="prop-card-actions">
                    <button className="btn sm" onClick={() => openPropostaPdf(p, lead, cliente, store)}><Icon name="file-pdf" size={12} /> PDF</button>
                    {(p.status === "rascunho") && <button className="btn sm" onClick={() => store.sendProposta(lead.id, p.id)}><Icon name="send" size={12} /> Enviar</button>}
                    {(p.status === "enviada" || p.status === "negociacao") && <button className="btn sm" onClick={() => store.updatePropostaStatus && store.updatePropostaStatus(lead.id, p.id, "negociacao")}><Icon name="message-circle" size={12} /> Em Negoci.</button>}
                    {!p.selecionadaFechamento && p.status !== "no_nomus" && p.status !== "perdida" && <button className="btn sm" onClick={() => store.selectPropostaForClosing(lead.id, p.id)}><Icon name="check-circle-2" size={12} /> Sel. fechamento</button>}
                    {/* Em voo NÃO oferece reenviar: o pedido pode existir no
                        ERP e só a resposta ter se perdido. Oferece conferir --
                        a função reconcilia pelo número que ficou gravado e
                        conclui sozinha se achar. Reenviar é o que duplicaria. */}
                    {p.erp?.emVoo
                      ? <button className="btn sm" style={{ color: "var(--color-warn)" }} onClick={() => setPendingErp(p.id)} title="Consultar o Nomus e concluir se o pedido já existir lá"><Icon name="refresh-cw" size={12} /> Conferir no Nomus</button>
                      : (p.status !== "no_nomus" && p.status !== "perdida" && <button className="btn sm" style={{ color: emissora.corDeep }} onClick={() => setReval(p)} disabled={p.modalidadeTransporte == null} title={p.modalidadeTransporte == null ? "Confirme a modalidade de transporte desta proposta antes de enviar ao Nomus" : "Conferir no Nomus e converter em pedido"}><Icon name="server" size={12} /> → Nomus</button>)}
                    {/* EDITAR (14/09). O editor sempre existiu -- PropostaBuilderModal
                        recebe `propostaBase` e o botao vira "Salvar" --, mas nada
                        no drawer o abria com uma proposta existente. O vendedor
                        so tinha "Rev." e "Duplicar", que criam proposta nova.

                        Sai depois de virar pedido no ERP: ali a proposta e o
                        documento que gerou o CP, e mexer nela faria o CRM
                        discordar do que foi faturado. Para esse caso o caminho
                        continua sendo a revisao. Decisao minha, reversivel -- a
                        alternativa seria deixar editar sempre. */}
                    {onEditProposta && p.status !== "no_nomus" && p.status !== "perdida"
                      && <button className="btn sm" onClick={() => onEditProposta(p)}><Icon name="pencil" size={12} /> Editar</button>}
                    {store.criarRevisao && p.status !== "rascunho" && <button className="btn sm" onClick={() => store.criarRevisao(lead.id, p.id)}><Icon name="copy" size={12} /> Rev.</button>}
                    {store.duplicarProposta && <button className="btn sm" onClick={() => store.duplicarProposta(lead.id, p.id)}><Icon name="files" size={12} /> Duplicar</button>}
                    <button className="icon-btn" title="Remover" onClick={() => store.removeProposta(lead.id, p.id)}><Icon name="trash" size={13} /></button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {reval && (
        <RevalidacaoModal
          modo="envio"
          itens={(reval.itens || []).map((it, i) => ({
            ...it, _id: it._id || "li_" + i, codigo: it.codigo || "", nome: it.nome || it.produto || "",
            precoUnitario: dinheiro(it.precoUnitario != null ? it.precoUnitario : (it.precoTabela || 0)),
          }))}
          tabela={store.getTabelaById(reval.tabelaId)}
          idEmpresa={(EMISSORAS_BY_ID[reval.emissoraId] || EMISSORAS[0]).idEmpresa}
          cta="Enviar ao Nomus"
          onClose={() => setReval(null)}
          // A conferencia so trava o que e erro (bloqueado, inativo, sem preço...);
          // preço diferente do Nomus vai como está, pelo fator (Kaio, 25/09).
          onConfirm={() => {
            const prop = reval;
            setReval(null);
            setPendingErp(prop.id);
          }} />
      )}
    </div>
  );
}

// ===== CTA contextual por estágio + Modal de entrada em produção =====
function getCtaConfig(stage) {
  const MAP = {
    ENTRADA:     { label: "Qualificar",            action: "qualificar",  icon: "check" },
    OUT_NOVA:    { label: "Qualificar",            action: "qualificar",  icon: "check" },
    QUALIFICADO: { label: "Agendar reunião",       action: "agendar",     icon: "calendar" },
    REUNIAO:     { label: "Criar proposta",        action: "proposta",    icon: "file-text" },
    ORCAMENTO:   { label: "Criar proposta",        action: "proposta",    icon: "file-text" },
    PROPOSTA:    { label: "Enviar proposta",       action: "enviar",      icon: "send" },
    NEGOCIACAO:  { label: "Marcar como ganho",     action: "ganho",       icon: "award" },
  };
  return MAP[stage] || null;
}

function ProducaoModal({ lead, onClose }) {
  const store = useStore();
  const [cp, setCp] = useStateDr(lead.pedidoNomus || "CP ");
  const [prev, setPrev] = useStateDr(lead.previsaoEntrega ? new Date(lead.previsaoEntrega + "T00:00:00") : null);
  const ok = cp.trim().length >= 4 && !!prev;
  const submit = async () => {
    if (!ok) { store.toast({ kind: "err", msg: "Informe o número CP e a previsão de entrega" }); return; }
    const iso = prev.toISOString().slice(0, 10);
    // GANHO, não uma etapa "PRODUCAO" no funil: produção acontece no PÓS-VENDA.
    // `moveLeadStage("GANHO")` já roteia o negócio para o pós-venda na etapa
    // Produção, avisa por toast e registra na linha do tempo — por isso este
    // modal não repete nenhuma dessas três coisas.
    // Houve uma etapa PRODUCAO no funil até 20/08; era duplicata do pós-venda e
    // saiu na migration 0028. Ver o comentário dela antes de recriar.
    //
    // `await` + checagem do retorno é obrigatório aqui: sem isso, o registro abaixo
    // ("Entrada em produção · pedido CP ...") entrava na timeline e o modal fechava
    // mesmo quando a RLS recusava o GANHO — a timeline afirmava um fato que não
    // aconteceu no banco (achado 2 da revisão final). Se a gravação falhar,
    // moveLeadStage já mostrou o toast de erro; aqui só resta manter o modal aberto
    // para o usuário tentar de novo.
    const moveu = await store.moveLeadStage(lead.id, "GANHO");
    if (!moveu) return;
    // Roda DEPOIS de `moveu` confirmar o GANHO. Historico: quando `updateLead`
    // era so memoria, esta chamada vinha ANTES da guarda e o card ficava com
    // `pedidoNomus` preenchido mesmo com a RLS recusando o GANHO -- o selo de
    // producao aparecia para um negocio que o banco nunca tocou (achado da 2ª
    // revisao final). Nao mova para antes do `await` de volta.
    // `await` e guarda, pelo mesmo motivo do `moveu` acima: desde 14/09 esta
    // chamada grava, e a RLS pode recusar. Sem a guarda, o card mostraria o selo
    // de produção (`isProd = !!lead.pedidoNomus`, src/kanban.jsx) e a timeline
    // registraria a entrada em produção de um pedido que o banco não aceitou.
    if (!await store.updateLead(lead.id, { pedidoNomus: cp.trim(), previsaoEntrega: iso })) return;
    // Este registro FICA: o roteamento automático não conhece o número do
    // pedido nem a data de entrega, que é justamente o que o modal captura.
    store.appendTimeline(lead.id, { tipo: "stage", autorTipo: "humano", autor: store.me?.nome, texto: <>Entrada em produção · pedido <strong>{cp.trim()}</strong> · entrega prevista <strong>{prev.toLocaleDateString("pt-BR")}</strong>.</>, when: "agora" });
    onClose();
  };
  return (
    <ModalShell title="Entrada em produção" icon="package" onClose={onClose} width={460} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={submit} disabled={!ok} style={{ opacity: ok ? 1 : 0.5 }}><Icon name="check" size={13} /> Confirmar produção</button>
      </>
    }>
      <div style={{ fontSize: 13, color: "var(--color-ink-soft)", marginBottom: 14 }}>
        Movendo <strong style={{ color: "var(--color-ink)" }}>{lead.empresa}</strong> para <strong>Produção</strong>. Confirme o número do pedido Nomus e a data prevista de entrega.
      </div>
      <FormField label="Número do Pedido Nomus (CP)" required>
        <Input icon="hash" placeholder="CP 09969" value={cp} onChange={e => setCp(e.target.value)} />
      </FormField>
      <FormField label="Previsão de entrega" required hint="datas futuras">
        <InlineDatePicker value={prev} onChange={setPrev} anchorLabel="Escolher data" />
      </FormField>
    </ModalShell>
  );
}

function DrawerCTA({ lead, onAddAlert, onNewProposta, onOpenProducao }) {
  const store = useStore();
  const podeMover = usePermission('pipeline.move_stage');
  const escreve = useEscreveNoLead();
  const cfg = getCtaConfig(lead.stage);
  if (!cfg) return null;
  const papel = (store.me?.papel ?? "admin").toLowerCase();
  const canProducao = papel === "backoffice" || papel === "admin";
  // O banco recusa (42501, 0088) quem muda de etapa sem `pipeline.move_stage`: o botão que muda a
  // etapa fica desligado, com o motivo, em vez de falhar no clique.
  // Lead de outra pessoa (quem o trouxe já o repassou): o banco só deixa o dono atual (ou o escopo team).
  const alheio = !escreve(lead);
  const semMover = (!podeMover || alheio) && (cfg.action === "qualificar" || cfg.action === "ganho" || cfg.action === "producao");
  const blocked = (cfg.action === "producao" && !canProducao) || semMover;
  // Critério de saída da etapa atual = gate da próxima etapa do funil.
  const stagesFunil = STAGES_BY_FUNNEL[lead.funil] || STAGES_PROJETOS;
  const iAtual = stagesFunil.findIndex(s => s.id === lead.stage);
  const proxima = iAtual >= 0 ? stagesFunil[iAtual + 1] : null;
  const gs = proxima ? gateStatus(lead, proxima.id, { propostas: store.getPropostasByLead(lead.id) }) : { ok: true, gate: null };

  const run = () => {
    switch (cfg.action) {
      case "qualificar":  store.moveLeadStage(lead.id, "QUALIFICADO"); break;
      case "agendar":     onAddAlert && onAddAlert(); break;
      case "proposta":    onNewProposta && onNewProposta(); break;
      case "enviar": {
        const props = store.getPropostasByLead(lead.id);
        const sel = props.find(p => p.selecionadaFechamento) || props[0];
        if (sel) store.sendProposta(lead.id, sel.id);
        else onNewProposta && onNewProposta();
        break;
      }
      case "ganho":       store.moveLeadStage(lead.id, "GANHO"); break;
      default: break;
    }
  };

  return (
    <div className="drawer-cta">
      <button className="btn primary drawer-cta-btn" onClick={run} disabled={blocked} title={semMover ? (alheio ? "Este negócio é de outra pessoa" : "Seu cargo não move o negócio de etapa") : blocked ? "Somente Backoffice pode mover Pedido → Produção" : cfg.label}>
        <Icon name={cfg.icon} size={14} /> {cfg.label}
        <Icon name="arrow-right" size={13} style={{ marginLeft: "auto", opacity: 0.7 }} />
      </button>
      {semMover && <div className="drawer-cta-hint"><Icon name="lock" size={10} /> {alheio ? "Este negócio é de outra pessoa." : "Seu cargo não move o negócio de etapa."}</div>}
      {blocked && !semMover && <div className="drawer-cta-hint"><Icon name="lock" size={10} /> Apenas Backoffice confirma a entrada em produção.</div>}
      {!blocked && gs.gate && (
        <div className={`drawer-cta-hint ${gs.ok ? "gate-ok" : "gate-falta"}`}>
          <Icon name={gs.ok ? "check-circle-2" : "shield-check"} size={10} />
          <span><strong>{proxima.name}:</strong> {gs.gate.criterio}{gs.ok ? "" : ` · falta ${gs.gate.exige.toLowerCase()}`}</span>
        </div>
      )}
    </div>
  );
}

// Confirmação da exclusão. A regra (só nas duas primeiras etapas) vem de src/exclusao-de-lead.js e é
// cobrada pelo banco (0090); aqui a pessoa fica sabendo o que perde, porque não há como desfazer.
function ExcluirLeadModal({ lead, onClose, onExcluido }) {
  const store = useStore();
  const [enviando, setEnviando] = useStateDr(false);
  const confirmar = async () => {
    setEnviando(true);
    const ok = await store.deleteLead(lead.id);
    setEnviando(false);
    onClose();
    if (ok && onExcluido) onExcluido();
  };
  return (
    <ModalShell title={`Excluir · ${lead.empresa}`} icon="trash" onClose={onClose} width={460} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn danger" onClick={confirmar} disabled={enviando}><Icon name="trash" size={13} /> Excluir lead</button>
      </>
    }>
      <div style={{ fontSize: 13, color: "var(--color-ink-soft)", lineHeight: 1.5 }}>
        Excluir <strong style={{ color: "var(--color-ink)" }}>{lead.empresa}</strong>? Isso apaga o lead e tudo o que está ligado a ele
        (tarefas, notas, anexos e propostas). Só é possível nas duas primeiras etapas do funil, e não pode ser desfeito.
      </div>
    </ModalShell>
  );
}

function LeadDrawer({ lead, onClose, onEdit, onAddNote, onAddAlert, onOpenCliente, onMarkLost, onPreviewPdf, onNewProposta, onEditProposta, onExpandToPage, onNewCliente }) {
  const store = useStore();
  // Perder/reabrir e repassar só aparecem a quem o banco deixa fazer (0087 e 0088).
  const permsPipe = usePermissions('pipeline.mark_lost', 'pipeline.handoff', 'pipeline.delete_lead');
  const escreve = useEscreveNoLead();
  const [excluindo, setExcluindo] = useStateDr(false);
  // Excluir: permissão, lead que é seu (ou escopo team) e só nas duas primeiras etapas (0090).
  const podeExcluir = permsPipe['pipeline.delete_lead'] && escreve(lead) && leadAdmiteExclusao(lead);
  const [tab, setTab] = useStateDr("dados");
  const [showProducao, setShowProducao] = useStateDr(false);
  // Ao adicionar uma nota, salta para a Timeline para a nota recém-criada ficar visível
  useEffectDr(() => {
    const onNote = (e) => { if (e.detail?.leadId === lead.id) setTab("historico"); };
    window.addEventListener("beam:note-added", onNote);
    return () => window.removeEventListener("beam:note-added", onNote);
  }, [lead.id]);
  const tabs = [
    { id: "dados",     label: "Dados",     icon: "user" },
    { id: "vinculos",  label: "Vínculos",  icon: "users" },
    { id: "tabela",    label: "Tabela",    icon: "file-spreadsheet" },
    { id: "propostas", label: "Propostas", icon: "file-text", pill: store.getPropostasByLead(lead.id).length },
    { id: "anexos",    label: "Anexos",    icon: "paperclip", pill: store.getAnexosByLead(lead.id).length },
    { id: "bant",      label: "BANT",      icon: "shield-check" },
    { id: "alerts",    label: "Tarefas",   icon: "check-circle-2", pill: store.alerts.filter(a => a.leadId === lead.id && !a.feito).length },
    { id: "historico", label: "Timeline",  icon: "history" },
    { id: "cadeia",    label: "Cadeia",    icon: "link",    pill: (lead.cadeia || []).length },
  ];
  const agentName = AGENTS.find(a => a.id === lead.agenteId)?.name;
  const owner = store.users.find(u => u.id === lead.ownerId);
  // Exclui o próprio usuário logado: store.users agora inclui quem está autenticado,
  // e "passar para si mesmo" é uma operação sem efeito que não deveria aparecer na lista.
  const closers = store.users.filter(u => u.papel === "closer" && u.id !== store.me.id);
  const cliente = lead.clienteId ? store.getClienteById(lead.clienteId) : null;
  const funnel = FUNNELS.find(f => f.id === lead.funil);
  const isPerdido = !!lead.perdido;
  const isGanho = lead.stage === "GANHO";

  return (
    <aside className="drawer gaveta-negocio" data-screen-label={`Lead Drawer / ${lead.empresa}`}>
      <div className="drawer-head">
        <div className="top">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="drawer-eyebrow">
              <Icon name={funnel?.icon || "compass"} size={11} />
              <span>{funnel?.name || "—"}</span>
              {cliente && <><span className="sep">·</span><button className="cli-link-eyebrow" onClick={() => onOpenCliente(cliente.id)}>{cliente.nome}</button></>}
            </div>
            <div className="title">{lead.empresa}</div>
          </div>
          <button className="icon-btn" onClick={onExpandToPage} title="Abrir área completa do negócio" style={{ marginRight: 4 }}><Icon name="maximize" size={15} /></button>
          <button className="icon-btn" onClick={onClose} title="Fechar"><Icon name="x" size={16} /></button>
        </div>

        {/* Linha de valor + status — informação crítica do negócio */}
        <div className="dh-value-row">
          <div className="dh-value">
            <span className="dh-value-num mono" style={{ color: lead.valor > 0 ? "var(--color-ok)" : "var(--color-ink-faint)" }}>
              {lead.valor > 0 ? brl(lead.valor) : "Valor a definir"}
            </span>
          </div>
          <div className="dh-status">
            <span className="dh-stage"><span className={`stage-dot stage-${lead.stage}`} style={{ width: 7, height: 7, borderRadius: "50%" }} /> {STAGE_NAMES[lead.stage]}</span>
            <HeatDot heat={lead.calor} withLabel />
          </div>
        </div>
        <div className="dh-meta">
          <OriginPill origin={lead.origem} agentName={agentName} compact={false} />
          <span className="sep">·</span>
          <span>{lead.diasNoEstagio}d no estágio</span>
        </div>

        {owner && (
          <div className="owner-flow">
            <div className="of-orig" title="Originador do lead — funil de origem preservado">
              {lead.originador?.tipo === "ia"
                ? <span className="of-badge ia"><Icon name="cpu" size={11} /> IA · {lead.originador.nome}</span>
                : <span className="of-badge sdr"><Icon name="user" size={11} /> SDR · {lead.originador?.nome || "—"}</span>}
            </div>
            <Icon name="chevron-right" size={12} style={{ color: "var(--color-ink-faint)" }} />
            <div className="of-owner">
              <div className="avatar" style={{ width: 20, height: 20, fontSize: 9, background: "var(--color-border-strong)", color: "var(--color-ink)" }}>{owner.initials}</div>
              <span><strong style={{ color: "var(--color-ink)" }}>{owner.nome}</strong> · {owner.papel}</span>
            </div>
          </div>
        )}
        {!isPerdido && !isGanho && (
          <div className="drawer-toolbar">
            <button className="btn sm" onClick={onAddAlert}><Icon name="bell" size={12} /> Alerta</button>
            <button className="btn sm" onClick={onAddNote}><Icon name="message-square" size={12} /> Nota</button>
            <button className="btn sm primary-soft" onClick={onNewProposta}><Icon name="file-text" size={12} /> Proposta</button>
            {closers.length > 0 && permsPipe['pipeline.handoff'] && escreve(lead) && (
              <Select value="" placeholder="Closer" variant="chip" icon="arrow-right" onChange={v => v && store.handoffToCloser(lead.id, v)}
                options={[{ value: "", label: "Passar p/ Closer" }, ...closers.map(c => ({ value: c.id, label: c.nome }))]} />
            )}
            {permsPipe['pipeline.mark_lost'] && escreve(lead) && <button className="btn sm danger ghost dt-perder" onClick={onMarkLost} title="Marcar negócio como perdido"><Icon name="x" size={13} /></button>}
            {podeExcluir && <button className="btn sm danger ghost dt-excluir" onClick={() => setExcluindo(true)} title="Excluir lead"><Icon name="trash" size={13} /></button>}
          </div>
        )}
        {!isPerdido && !isGanho && (
          <DrawerCTA lead={lead} onAddAlert={onAddAlert} onNewProposta={onNewProposta} onOpenProducao={() => setShowProducao(true)} />
        )}
        {isPerdido && lead.motivoPerda && (
          <div className="lost-banner">
            <Icon name="alert-triangle" size={13} />
            <div style={{ flex: 1 }}>
              <div><strong>Perdido em {STAGE_NAMES[lead.stage]} · {lead.motivoPerda}</strong></div>
              {lead.obsPerda && <div className="obs">"{lead.obsPerda}"</div>}
            </div>
            {permsPipe['pipeline.mark_lost'] && escreve(lead) && <button className="btn sm" onClick={() => store.reopenLead(lead.id)}><Icon name="refresh" size={12} /> Reabrir</button>}
          </div>
        )}
      </div>
      <div className="drawer-tabs">
        {tabs.map(t => (
          <button key={t.id} className={`drawer-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)} title={t.label}>
            <Icon name={t.icon} size={14} />
            <span className="dt-label">{t.label}</span>
            {t.pill > 0 && <span className="pill">{t.pill}</span>}
          </button>
        ))}
      </div>
      <div className="drawer-body">
        {tab === "dados"     && <TabDados lead={lead} agentName={agentName} onEdit={onEdit} onAddNote={onAddNote} />}
        {tab === "vinculos"  && <TabVinculos lead={lead} onOpenCliente={onOpenCliente} onNewCliente={onNewCliente} />}
        {tab === "tabela"    && <TabTabelaPrecos lead={lead} />}
        {tab === "propostas" && <TabPropostas lead={lead} onNewProposta={onNewProposta} onEditProposta={onEditProposta} />}
        {tab === "anexos"    && <TabAnexos lead={lead} onPreviewPdf={onPreviewPdf} />}
        {tab === "bant"      && <TabBant lead={lead} />}
        {tab === "alerts"    && <TabAlertas lead={lead} onAddAlert={onAddAlert} />}
        {tab === "historico" && <TabHistorico lead={lead} onAddNote={onAddNote} />}
        {tab === "cadeia"    && <><AtribuicaoResumo lead={lead} /><CadeiaTimeline cadeia={(lead.cadeia || []).map(e => (e.atorNome ? e : { ...e, atorNome: store.users.find(u => u.id === e.atorId)?.nome || "—" }))} /></>}
      </div>
      {showProducao && <ProducaoModal lead={lead} onClose={() => setShowProducao(false)} />}
      {excluindo && <ExcluirLeadModal lead={lead} onClose={() => setExcluindo(false)} onExcluido={onClose} />}
    </aside>
  );
}

// Linha de valores do cartão da proposta. Mostrava `ICMS {p.icms}`, campo que nunca foi gravado: o vendedor
// lia um número que não existia. Agora é o ICMS-ST ESTIMADO (0093) ou "a confirmar" -- nunca zero por falta
// de dado. O `Total` não muda; o total com ICMS-ST vem ao lado, só quando a estimativa está completa.
function ResumoDeValoresDaProposta({ p }) {
  const st = rotuloICMSST(p);
  return (
    <div className="prop-totline">
      <span>Produtos {brlExato(p.totalProduto)} · Frete {brlExato(p.frete)} · {st.texto} {st.valor}{p.descontoGlobal > 0 ? ` · Desc. global ${p.descontoGlobal}%` : ""}</span>
      <strong className="mono">Total {brlExato(p.total)}{st.totalComST != null ? ` · com ICMS-ST (estimado) ${brlExato(st.totalComST)}` : ""}</strong>
    </div>
  );
}

// Decisão do Kaio (06/10): proposta com ICMS-ST "a confirmar" PODE ser salva e enviada, com aviso. O aviso
// aparece só enquanto é rascunho -- depois de enviada, repeti-lo seria ruído.
function AvisoICMSST({ p }) {
  const st = rotuloICMSST(p);
  if (st.completo || p.status !== "rascunho") return null;
  const n = st.pendentes;
  return (
    <div className="prop-erp-line" style={{ justifyContent: "flex-start", background: "#FEF3C7", color: "#B45309", fontWeight: 600 }}>
      <Icon name="alert-triangle" size={11} />
      <span> ICMS-ST a confirmar{n > 0 ? ` em ${n} ${n === 1 ? "item" : "itens"}` : ""}: o documento sai com "a confirmar". Consulte o financeiro antes de enviar ao cliente.</span>
    </div>
  );
}

// Proposta ANTERIOR ao registro da modalidade (migration 0096): `modalidadeTransporte` NULL. O pedido ao Nomus saia FOB por
// padrao, qualquer que fosse a escolha na tela; agora a tela NAO presume nada e o vendedor confirma aqui (decisao do Kaio,
// 08/10: "exigir a confirmacao antes do envio"). Nada vem pre-selecionado de proposito: confirmar e escolher. A trava de
// verdade, caso alguem chegue ao envio por outro caminho, esta em store.approvePropostaToERP.
function ConfirmarModalidade({ p, onConfirmar }) {
  if (p.modalidadeTransporte != null || p.status === "no_nomus" || p.status === "perdida" || p.erp?.pedidoId) return null;
  return (
    // A disposicao (quebra de linha, icone na linha do texto, seletor embaixo) esta em `.prop-aviso-modalidade` no
    // styles.css, nao em estilo inline: com `flex-wrap` inline o texto longo ia para a linha de baixo e deixava o icone sozinho.
    <div className="prop-erp-line prop-aviso-modalidade" data-aviso="modalidade-nao-confirmada"
      style={{ justifyContent: "flex-start", background: "#FEF3C7", color: "#B45309", fontWeight: 600 }}>
      <Icon name="alert-triangle" size={11} />
      <span>Modalidade de transporte não confirmada: esta proposta é anterior ao registro da modalidade. Confirme para poder enviar ao Nomus.</span>
      {/* O Select do projeto (modals.jsx) nao aceita `aria-label`: o rotulo acessivel fica no grupo que o envolve. */}
      <div className="prop-aviso-seletor" role="group" aria-label="Confirmar a modalidade de transporte">
        <Select
          value=""
          onChange={(v) => { if (v !== "") onConfirmar(Number(v)); }}
          options={[{ value: "", label: "Escolher a modalidade…" }, ...TRANSPORTE_MODALIDADES.map((m) => ({ value: m.id, label: m.nome }))]}
        />
      </div>
    </div>
  );
}

export { LeadDrawer, SeloDeEnvio, ItensDaProposta, autoriaDaProposta, ResumoDeValoresDaProposta, AvisoICMSST, ConfirmarModalidade };

// ===== NegócioPage: área completa full-page do negócio (espelha ClientePage) =====
// Reaproveita os mesmos blocos do drawer (Dados, Vínculos, Tabela, Propostas, Anexos, BANT,
// Tarefas, Timeline) em cards colapsáveis, com hero + KPIs + ações.
function NegocioPage({ leadId, onBack, onEdit, onAddNote, onAddAlert, onNewProposta, onEditProposta, onOpenCliente, onPreviewPdf, onMarkLost, onNewCliente }) {
  const store = useStore();
  const permsPipe = usePermissions('pipeline.mark_lost', 'pipeline.delete_lead');
  const escreve = useEscreveNoLead();
  const [excluindo, setExcluindo] = useStateDr(false);
  const lead = store.leads.find(l => l.id === leadId);
  if (!lead) return (
    <div className="main">
      <div className="acesso-negado">
        <div className="an-ic"><Icon name="search" size={22} /></div>
        <div className="an-tt">Negócio não encontrado</div>
        <div className="an-sub">Este negócio não existe mais ou o link está desatualizado.</div>
        <button className="btn primary" style={{ marginTop: 4 }} onClick={onBack}><Icon name="chevron-left" size={13} /> Voltar ao pipeline</button>
      </div>
    </div>
  );

  const cliente = lead.clienteId ? store.getClienteById(lead.clienteId) : null;
  const funnel = FUNNELS.find(f => f.id === lead.funil);
  const agentName = AGENTS.find(a => a.id === lead.agenteId)?.name;
  const owner = store.users.find(u => u.id === lead.ownerId);
  const isPerdido = !!lead.perdido;
  const isGanho = lead.stage === "GANHO";
  const wpp = whatsappLink(lead.telefone);

  const propostas = store.getPropostasByLead(lead.id);
  const anexos = store.getAnexosByLead(lead.id);
  const tarefasPend = store.alerts.filter(a => a.leadId === lead.id && !a.feito).length;
  const propSel = propostas.find(p => p.selecionadaFechamento);
  const stageColor = `var(--stage-${lead.stage}, var(--color-accent))`;
  // Perder e Excluir ficam num grupo à parte (cp-acoes-perigo): no celular ele vira uma linha própria, longe do Editar.
  const podePerder = !isPerdido && !isGanho && permsPipe['pipeline.mark_lost'] && escreve(lead);
  const podeExcluir = !isPerdido && !isGanho && permsPipe['pipeline.delete_lead'] && escreve(lead) && leadAdmiteExclusao(lead);

  return (
    <div className="main main-scroll cliente-page negocio-pagina" data-screen-label={`Negócio /${lead.empresa}`}>
      {/* Hero */}
      <div className="cp-hero" style={{ background: "linear-gradient(180deg, var(--color-accent-soft) 0%, var(--color-bg) 100%)" }}>
        <button className="btn ghost sm" onClick={onBack}><Icon name="chevron-left" size={13} /> Voltar para o Pipeline</button>
        <div className="cp-hero-main">
          <div className="neg-hero-badge" style={{ background: stageColor }}>
            <Icon name={funnel?.icon || "layout-kanban"} size={26} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="cp-hero-title">{lead.empresa}</div>
            <div className="cp-hero-meta">
              <span className="neg-stage-pill meta-estagio"><span className={`stage-dot stage-${lead.stage}`} /> {STAGE_NAMES[lead.stage]}</span>
              {funnel && <><span className="cp-meta-sep">·</span><span className="meta-funil"><Icon name={funnel.icon} size={11} /> {funnel.name}</span></>}
              {cliente && <><span className="cp-meta-sep">·</span><button className="cli-link-eyebrow" onClick={() => onOpenCliente(cliente.id)}><Icon name="users" size={11} /> {cliente.nome}</button></>}
              <span className="cp-meta-sep">·</span>
              <span className="meta-dias"><Icon name="history" size={11} /> {lead.diasNoEstagio}d no estágio</span>
              <span className="meta-calor"><HeatDot heat={lead.calor} withLabel /></span>
              <span className="meta-quebra" aria-hidden="true" />
            </div>
          </div>
          <div className="cp-hero-actions">
            {wpp && <a href={wpp} target="_blank" rel="noreferrer" className="btn"><Icon name="message-square" size={13} /> WhatsApp</a>}
            {!isPerdido && !isGanho && <button className="btn" onClick={onAddAlert}><Icon name="bell" size={13} /> Tarefa</button>}
            {!isPerdido && !isGanho && <button className="btn" onClick={onNewProposta}><Icon name="file-text" size={13} /> Proposta</button>}
            {escreve(lead) && <button className="btn primary" onClick={onEdit}><Icon name="pencil" size={13} /> Editar</button>}
            {(podePerder || podeExcluir) && (
              <div className="cp-acoes-perigo">
                {podePerder && <button className="btn danger ghost" onClick={onMarkLost}><Icon name="x" size={13} /> Perder</button>}
                {podeExcluir && <button className="btn danger ghost dt-excluir" onClick={() => setExcluindo(true)}><Icon name="trash" size={13} /> Excluir</button>}
              </div>
            )}
          </div>
        </div>
        {excluindo && <ExcluirLeadModal lead={lead} onClose={() => setExcluindo(false)} onExcluido={onBack} />}
        <div className="cp-hero-kpis">
          <div className="cp-kpi"><div className="lbl">Valor do negócio</div><div className="val mono" style={{ color: lead.valor > 0 ? "var(--color-ok)" : "var(--color-ink-soft)" }}>{lead.valor > 0 ? brl(lead.valor) : "—"}</div></div>
          <div className="cp-kpi"><div className="lbl">Propostas</div><div className="val mono">{propostas.length}<span className="sub">{propSel ? ` · ${propSel.controle} p/ fechar` : ""}</span></div></div>
          <div className="cp-kpi"><div className="lbl">Próxima ação</div><div className="val" style={{ fontSize: 13 }}><ProximaAcaoInline lead={lead} compact /></div></div>
          <div className="cp-kpi"><div className="lbl">Tarefas pendentes</div><div className="val mono" style={{ color: tarefasPend > 0 ? "var(--color-warn)" : "var(--color-ink)" }}>{tarefasPend}</div></div>
        </div>
        {isPerdido && lead.motivoPerda && (
          <div className="lost-banner" style={{ marginTop: 12 }}>
            <Icon name="alert-triangle" size={13} />
            <div style={{ flex: 1 }}><strong>Perdido em {STAGE_NAMES[lead.stage]} · {lead.motivoPerda}</strong>{lead.obsPerda && <div className="obs">"{lead.obsPerda}"</div>}</div>
            {permsPipe['pipeline.mark_lost'] && escreve(lead) && <button className="btn sm" onClick={() => store.reopenLead(lead.id)}><Icon name="refresh" size={12} /> Reabrir</button>}
          </div>
        )}
        <div className="cp-hero-hint">
          <Icon name="grip" size={11} />
          <span>Visão completa do negócio · clique em qualquer bloco para colapsar.</span>
        </div>
      </div>

      {/* Grid */}
      <div className="cp-grid">
        <div className="cp-col">
          <CollapsibleCard id="neg-dados" title="Dados do negócio" icon="user">
            <TabDados lead={lead} agentName={agentName} onEdit={onEdit} onAddNote={onAddNote} />
          </CollapsibleCard>
          <CollapsibleCard id="neg-vinculos" title="Vínculos" icon="users">
            <TabVinculos lead={lead} onOpenCliente={onOpenCliente} onNewCliente={onNewCliente} />
          </CollapsibleCard>
          <CollapsibleCard id="neg-bant" title="Qualificação BANT" icon="shield-check">
            <TabBant lead={lead} />
          </CollapsibleCard>
          <CollapsibleCard id="neg-tarefas" title="Tarefas" icon="check-circle-2" badge={tarefasPend > 0 ? tarefasPend : null}>
            <TabAlertas lead={lead} onAddAlert={onAddAlert} />
          </CollapsibleCard>
        </div>
        <div className="cp-col">
          <CollapsibleCard id="neg-propostas" title="Propostas" icon="file-text" badge={propostas.length || null}>
            <TabPropostas lead={lead} onNewProposta={onNewProposta} onEditProposta={onEditProposta} />
          </CollapsibleCard>
          <CollapsibleCard id="neg-tabela" title="Tabela de preços" icon="file-spreadsheet">
            <TabTabelaPrecos lead={lead} />
          </CollapsibleCard>
          <CollapsibleCard id="neg-anexos" title="Anexos" icon="paperclip" badge={anexos.length || null}>
            <TabAnexos lead={lead} onPreviewPdf={onPreviewPdf} />
          </CollapsibleCard>
          <CollapsibleCard id="neg-timeline" title="Timeline" icon="history">
            <TabHistorico lead={lead} onAddNote={onAddNote} />
          </CollapsibleCard>
        </div>
      </div>
    </div>
  );
}

export { NegocioPage };
